import { getSelectedSessionId } from "@/sdk";
import { getSharedApiClient } from "@/shared-utils";

export const EXPERT_CHANNEL_INDEX: [number, number] = [0, 2];
export const CUSTOMER_CHANNEL_INDEX: [number, number] = [1, 2];

export const DEFAULT_REALTIME_TOOLS =
    "gaia-post-call-summary,gaia-device-cues,gaia-sales-checklist-mentions,gai-proactive,gaia-engagement-question-checklist";

export type Speaker = "expert" | "customer";

export interface MockConversationSnippet {
    speaker: Speaker;
    transcript: string;
}

export interface MockConversation {
    id: string;
    name: string;
    description: string;
    snippets: MockConversationSnippet[];
}

export interface BuildPayloadArgs {
    sessionId: string;
    agentId: string;
    employeeId: string;
    partner: string;
    client: string;
    transcript: string;
    realtimeTools: string;
    speaker: Speaker;
}

function randomStreamSid(): string {
    return `MZ${crypto.randomUUID().replaceAll("-", "")}`;
}

function buildCustomParameters(args: {
    sessionId: string;
    agentId: string;
    employeeId: string;
    partner: string;
    client: string;
    realtimeTools: string;
}) {
    return {
        AgentID: args.agentId,
        Client: args.client,
        Language: "en-us",
        LineOfBusiness: "SOLUTO",
        MDN: "**Redacted**",
        customer_callSid: args.sessionId,
        employeeId: args.employeeId,
        realtime_tools: args.realtimeTools,
        sessionId: args.sessionId,
        isConnectCall: true,
        PARTNER: args.partner.toLowerCase(),
    };
}

export function buildTranscriptEventPayload({
    sessionId,
    agentId,
    employeeId,
    partner,
    client,
    transcript,
    realtimeTools,
    speaker,
}: BuildPayloadArgs) {
    const streamSid = randomStreamSid();
    return {
        callSid: sessionId,
        customParameters: buildCustomParameters({ sessionId, agentId, employeeId, partner, client, realtimeTools }),
        event: {
            channel: {
                alternatives: [{ transcript }],
            },
            channel_index: speaker === "expert" ? EXPERT_CHANNEL_INDEX : CUSTOMER_CHANNEL_INDEX,
            metadata: {
                extra: {
                    agent_login: agentId,
                    callsid: sessionId,
                    streamsid: streamSid,
                },
            },
        },
        streamSid,
        timestamp: Date.now(),
        type: "transcript" as const,
    };
}

export type TranscriptEventPayload = ReturnType<typeof buildTranscriptEventPayload>;

export async function submitTranscriptEvent(payload: TranscriptEventPayload) {
    return getSharedApiClient(getSelectedSessionId())
        .post("api/event-producer/events/twilio-real-time", { json: payload })
        .json<{ status: string }>();
}

export async function fetchMockConversations(): Promise<MockConversation[]> {
    const res = await getSharedApiClient(getSelectedSessionId())
        .get("api/event-producer/events/twilio-real-time/mock-conversations")
        .json<{ conversations: MockConversation[] }>();
    return res.conversations;
}
